import { Badge, Box, Button, Card, Center, Divider, Flex, Spinner, useMediaQuery } from "@chakra-ui/react";
import {
  Banner,
  DateRangePicker,
  Icons,
  ToolTip,
  Typography,
  useFlowBuilderSession,
} from "@reconvert/react-ui-component";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageType, PreMadeTemplateSlotMapping, PreMadeTemplateType } from "@reconvert/reconvert-utils";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  handleDateRangePicker,
  selectAppState,
  toggleAnalyticsView,
  toggleHelpModal,
  toggleWidgetView,
} from "../../../app/slice/appSlice";
import { PageNames, ShopifyCheckoutEditorPage, ShopifyCheckoutEditorPageCollection } from "../../../app/slice/appSlice.model";
import { selectConditionsState } from "../../../app/slice/conditionSlice";
import { selectSlotsState } from "../../../app/slice/slotSlice";
import { selectBannerState } from "../../../app/slice/bannerSlice";
import { APP_PLATFORM, SHOPIFY_RECONVERT_THEME_APP_EXTENSION_ID } from "../../../config";
import useConditions from "../../../hooks/useConditions";
import { Platform, Variant } from "../../../models";
import EmptyCard from "./EmptyCard";
import { getAllVariantAnalytics } from "../../../app/slice/analyticsSlice";
import useVariants from "../../../hooks/useVariants";
import useUnsavedBar from "../../../hooks/useUnsavedBar";
import PreMadeTemplateModal from "../../PreMadeTemplateModal/PreMadeTemplateModal";
import useSlots from "../../../hooks/useSlots";
import { SlotVariant } from "../../SlotVariant";
import useIsActiveOnPage, { PagePurpose } from "../../../hooks/useIsActiveOnPage";
import { RECONVERT_IO_HELP_URL } from "../../../config";
import SlotBanner from "../../ProductPage/components/SlotBanner";

const placeSlotLink = {
  [PageType.PRE_PURCHASE_PRODUCT_PAGE]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-place-slots-in-the-shopify-editor-8qknth/reader?bust=1707148053284`,
    title: `How to set Upsell.com as your pre purchase upsell app | Shopify`,
  },

  [PageType.PRE_PURCHASE_CART_PAGE]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-place-slots-in-the-shopify-editor-8qknth/reader?bust=1707148053284`,
    title: `How to set Upsell.com as your pre purchase upsell app | Shopify`,
  },

  [PageType.PRE_PURCHASE_CART_DRAWER_PAGE]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-place-slots-in-the-shopify-editor-8qknth/reader?bust=1707148053284`,
    title: `How to set Upsell.com as your pre purchase upsell app | Shopify`,
  },

  [PageType.THANK_YOU_PAGE]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-create-and-place-slots-in-upsellcom-shopify-mcbrs9/reader?bust=1716461509072`,
    title: `How to place a Upsell.com slot on your thank you page | Shopify`,
  },

  [PageType.ORDER_STATUS_PAGE]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-create-and-place-slots-in-upsellcom-shopify-mcbrs9/reader?bust=1716461618198`,
    title: `How to place a Upsell.com slot on your order status page | Shopify`,
  },

  [PageType.CHECKOUT]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-create-and-place-slots-in-upsellcom-shopify-mcbrs9/reader?bust=1716461728531`,
    title: `How to place a Upsell.com slot on your checkout | Shopify`,
  },
  [PageType.POST_PURCHASE_PAGE1]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-set-reconvert-as-your-post-purchase-upsell-app-shopify-1km1dzw/reader?bust=1716461217564`,
    title: `How to set Upsell.com as your post purchase upsell app | Shopify`,
  },

  [PageType.POST_PURCHASE_PAGE2]: {
    url: `${RECONVERT_IO_HELP_URL}/en/article/how-to-set-reconvert-as-your-post-purchase-upsell-app-shopify-1km1dzw/reader?bust=1716461217564`,
    title: `How to set Upsell.com as your post purchase upsell app | Shopify`,
  },

  [PageType.FORM_EDITOR]: {
    url: '',
    title: '',
  },
};

const getSlotHeaderLink = (platformStoreId: string, page: PageType) => {
  // This value can be extracted from .env (SHOPIFY_RECONVERT_THEME_APP_EXTENSION_ID) which gets generated during deployment from https://gitlab.com/reconvert/reconvert-shopify-extensions
  const themeAppExtensionId = SHOPIFY_RECONVERT_THEME_APP_EXTENSION_ID;

  switch (page) {
    case PageType.PRE_PURCHASE_CART_DRAWER_PAGE:
      return `https://${platformStoreId}/admin/themes/current/editor?context=apps&template=product&activateAppId=${themeAppExtensionId}/cart-drawer`;

    case PageType.PRE_PURCHASE_CART_PAGE:
      return `https://${platformStoreId}/admin/themes/current/editor?template=cart&addAppBlockId=${themeAppExtensionId}/slot&target=newAppsSection`;

    case PageType.PRE_PURCHASE_PRODUCT_PAGE:
      return `https://${platformStoreId}/admin/themes/current/editor?template=product&addAppBlockId=${themeAppExtensionId}/slot&target=newAppsSection`;

    case PageType.POST_PURCHASE_PAGE1:
    case PageType.POST_PURCHASE_PAGE2:
      return `https://${platformStoreId}/admin/settings/checkout#post-purchase`;
    default:
      return "";
  }
};

const HelpLink = memo(({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <Typography variant="small" color="link.color" fontWeight={500} as="a" pe="16px" cursor="pointer" onClick={onClick}>
    {children}
  </Typography>
));

const ExternalLink = memo(({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <Flex justifyContent="space-between" alignItems="center" onClick={onClick}>
    <Typography variant="small" as="a" cursor="pointer" color="link.color" pe={2} fontWeight={500}>
      {children}
    </Typography>
    <Icons.ExternalLink cursor="pointer" className="externalLink" w={4} h={4} />
  </Flex>
));

const SlotsHeaderLinks = () => {
  const { activePage, checkoutProfileId } = useAppSelector(selectAppState);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedCondition } = useConditions();

  const handleHelpClick = useCallback(() => {
    dispatch(
      toggleHelpModal({
        link: placeSlotLink[activePage!]?.url,
        title: t(placeSlotLink[activePage!]?.title),
      }),
    );
  }, [dispatch, activePage, t]);

  const handleEditorClick = useCallback(() => {
    window.open(getSlotHeaderLink(selectedCondition?.platformStoreId!, activePage!), "_blank");
  }, [selectedCondition?.platformStoreId, activePage]);

  const onGoToEditor = useCallback(() => {
    let queryParams = "";

    if (ShopifyCheckoutEditorPage[activePage!]) {
      queryParams = `?page=${ShopifyCheckoutEditorPage[activePage!]}`;
    }

    if (ShopifyCheckoutEditorPageCollection[activePage!]) {
      queryParams += `&collection=${ShopifyCheckoutEditorPageCollection[activePage!]}`;
    }

    if (checkoutProfileId) {
      window.open(
        `https://admin.shopify.com/store/${selectedCondition?.platformStoreId?.replaceAll(".myshopify.com", "")}/settings/checkout/editor/profiles/${checkoutProfileId}${queryParams}&context=apps`,
      );
    } else {
      window.open(
        `https://${selectedCondition?.platformStoreId}/admin/settings/checkout/editor${queryParams}&context=extensibility&extensionPicker=true`,
        "_blank",
      );
    }
  }, [activePage, selectedCondition?.platformStoreId, checkoutProfileId]);

  switch (activePage) {
    case PageType.PRE_PURCHASE_PRODUCT_PAGE:
      return (
        <Flex>
          <HelpLink onClick={handleHelpClick}>{t("Learn more about pre purchase pages")}</HelpLink>
          <ExternalLink onClick={handleEditorClick}>{t("Place your slots in the Theme editor")}</ExternalLink>
        </Flex>
      );
    case PageType.PRE_PURCHASE_CART_PAGE:
      return (
        <Flex>
          <HelpLink onClick={handleHelpClick}>{t("Learn more about pre purchase pages")}</HelpLink>
          <ExternalLink onClick={handleEditorClick}>{t("Place your slots in the Theme editor")}</ExternalLink>
        </Flex>
      );
    case PageType.PRE_PURCHASE_CART_DRAWER_PAGE:
      return (
        <Flex>
          <HelpLink onClick={handleHelpClick}>{t("Learn more about pre purchase pages")}</HelpLink>
          <ExternalLink onClick={handleEditorClick}>{t("Enable slots on the cart drawer")}</ExternalLink>
        </Flex>
      );
    case PageType.CHECKOUT:
      return (
        <Flex>
          <HelpLink onClick={handleHelpClick}>{t("How to place a slot?")}</HelpLink>
          <ExternalLink onClick={onGoToEditor}>{t("Place your slots in the Shopify editor")}</ExternalLink>
        </Flex>
      );
    case PageType.POST_PURCHASE_PAGE1:
    case PageType.POST_PURCHASE_PAGE2:
      return (
        <Flex>
          <HelpLink onClick={handleHelpClick}>{t("Learn more about post purchase pages")}</HelpLink>
          <ExternalLink onClick={handleEditorClick}>{t("Set Upsell.com as your post purchase app")}</ExternalLink>
        </Flex>
      );

    case PageType.THANK_YOU_PAGE:
      return (
        <Flex>
          <HelpLink onClick={handleHelpClick}>{t("How to place a slot?")}</HelpLink>
          <ExternalLink onClick={onGoToEditor}>{t("Place your slots in the Shopify editor")}</ExternalLink>
        </Flex>
      );

    case PageType.ORDER_STATUS_PAGE:
      return (
        <Flex>
          <HelpLink onClick={handleHelpClick}>{t("How to place a slot?")}</HelpLink>
          <ExternalLink onClick={onGoToEditor}>{t("Place your slots in the Shopify editor")}</ExternalLink>
        </Flex>
      );

    default:
      return <></>;
  }
};

const Slots = () => {
  const [showPreMadeTemplateModal, setShowPreMadeTemplateModal] = useState(false);
  const {
    isSidebarCollapased,
    activePage,
    dateRange,
    view: { isWidgetView, isAnalyticView },
    checkoutProfileId,
  } = useAppSelector(selectAppState);
  const { loading: loadingSlots, slots, canCreateSlot, creatingSlot } = useAppSelector(selectSlotsState);
  const { loading, loadingCondition, selectedConditionId } = useAppSelector(selectConditionsState);
  const { slotStatuses, isBannerLoading } = useAppSelector(selectBannerState);
  const { getCanCreateSlotFromSession, removeCanCreateSlotInSession } = useFlowBuilderSession();
  const { deletingVariant, handleDeleteVariant } = useVariants();
  const dispatch = useAppDispatch();
  const { selectedCondition } = useConditions();
  const { preventAction } = useUnsavedBar();

  const { handleCreateSlot } = useSlots();
  const { t } = useTranslation();
  const [isWidth1090] = useMediaQuery("(min-width: 1090px)");
  const [isSmallScreen] = useMediaQuery("(max-width: 1280px)");
  const [containerWidth, setContainerWidth] = useState<null | number>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const learnMoreAboutPagePurpose = useIsActiveOnPage(PagePurpose.LEARN_MORE_ABOUT_PAGES, activePage!);
  const isPrePurchasePages = useIsActiveOnPage(PagePurpose.PRE_PURCHASE_PAGES, activePage!);
  const isPageLoading = loadingSlots || loading || loadingCondition;
  const bannerType = "warning";

  const hasSlotCreationHandledRef = useRef(false);

  const handleSlotCreationBySlotNumber = useCallback(
    (statuses?: any[] | React.MouseEvent) => {
      const slotStatusList = Array.isArray(statuses) ? statuses : slotStatuses || [];

      const foundSlot = slotStatusList.find(
        status => status.location === activePage && status.slotActiveOnPage && !status.slotDefined,
      );

      if (foundSlot) {
        // Create a copy to avoid mutating Redux state
        const slot = { ...foundSlot };

        const templateSlotNo = PreMadeTemplateSlotMapping[slot?.slotType as PreMadeTemplateType]?.slotNo;

        handleCreateSlot(slot?.slotNo, templateSlotNo, null);
      }
    },
    [slotStatuses, activePage, handleCreateSlot],
  );

  // Handle slot creation when slotStatuses are loaded from Redux
  useEffect(() => {
    if (isBannerLoading || hasSlotCreationHandledRef.current) {
      return;
    }

    const canCreateSlotSession = getCanCreateSlotFromSession();

    if (canCreateSlotSession && slotStatuses && slotStatuses.length > 0 && selectedConditionId) {
      try {
        handleSlotCreationBySlotNumber(slotStatuses);
      } catch (error) {
        console.error("Error while handling slot creation modal", error);
      } finally {
        hasSlotCreationHandledRef.current = true;
        removeCanCreateSlotInSession();
      }
    }
  }, [
    selectedConditionId,
    slotStatuses,
    isBannerLoading,
    getCanCreateSlotFromSession,
    removeCanCreateSlotInSession,
    handleSlotCreationBySlotNumber,
  ]);

  useEffect(() => {
    if (selectedConditionId && isAnalyticView) {
      dispatch(getAllVariantAnalytics(selectedConditionId));
    }
  }, [dispatch, selectedConditionId, dateRange.startDate, dateRange.endDate, isAnalyticView]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef?.current) {
        setContainerWidth(containerRef?.current?.offsetWidth);
      }
    };

    handleResize();
    window.addEventListener("resize", () => handleResize());

    return () => window.removeEventListener("resize", handleResize);
  }, [containerRef?.current?.offsetWidth, isSidebarCollapased, deletingVariant, handleDeleteVariant]);

  const measuredRef = useCallback(
    (node: any) => {
      if (node !== null && !containerWidth) {
        setContainerWidth(node.getBoundingClientRect().width);
      }
    },
    [containerRef?.current?.offsetWidth],
  );

  const variantsBySlot = useMemo(() => {
    const groupedVariants: { [key: string]: Variant[] } = {};

    (selectedCondition?.variants || []).forEach((variant: Variant) => {
      const slotId = variant?.slotId;

      if (!groupedVariants[slotId]) {
        groupedVariants[slotId] = [];
      }

      groupedVariants[slotId].push(variant);
    });

    return groupedVariants;
  }, [selectedCondition]);

  const headerTitle: Record<PageType, string> = {
    [PageType.CHECKOUT]: "Checkout slots",
    [PageType.POST_PURCHASE_PAGE1]: "Post purchase - 1st page slots",
    [PageType.POST_PURCHASE_PAGE2]: "Post purchase - 2nd page slots",
    [PageType.THANK_YOU_PAGE]: "Thank you page slots",
    [PageType.ORDER_STATUS_PAGE]: "Order status page slots",
    [PageType.PRE_PURCHASE_PRODUCT_PAGE]: "Product page slots",
    [PageType.PRE_PURCHASE_CART_PAGE]: "Cart page slots",
    [PageType.PRE_PURCHASE_CART_DRAWER_PAGE]: "Cart drawer slots",
    [PageType.FORM_EDITOR]: '',
  };

  const [shortcut, setShortCut] = useState<string | null>(t("Last 30 days"));
  const [isAnalyticsHovered, setIsAnalyticsHovered] = useState(false);
  const [isWidgetHovered, setIsWidgetHovered] = useState(false);

  const createSlot = useCallback(() => {
    if (APP_PLATFORM === Platform.Shopify) {
      preventAction(() => setShowPreMadeTemplateModal(true));
    } else {
      preventAction(() => handleCreateSlot());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCreateSlot, preventAction]);

  const undefinedSlot = slotStatuses?.find(
    (status: any) => canCreateSlot && status.slotActiveOnPage && !status.slotDefined && status.location === activePage,
  );

  return (
    <Box pt={7} flex={1} pl={4} pr={"24px"} display="flex" flexDirection="column" height="100%" overflow="hidden">
      <Box flexShrink={0}>
        <Flex
          justifyContent={"space-between"}
          alignItems={isSidebarCollapased ? "center" : "self-start"}
          flexDirection="column"
        >
          <Flex w="100%" flexDirection={"column"} gap="2">
            <Flex alignItems={"center"} justifyContent="space-between">
              <Flex alignItems="center" wrap={"wrap"}>
                <Typography variant="h3-medium" pr={2}>
                  {t(headerTitle[activePage!])}
                </Typography>
                <ToolTip
                  placement="bottom"
                  label={t(
                    "Your widgets are stored in 'slots'. Once a condition is met, related slots display on {{pageName}}{{pageSuffix}}." +
                      (APP_PLATFORM === Platform.Shopify
                        ? " Use the Shopify Editor to position slots on the {{pageName}}{{pageSuffix}}."
                        : ""),
                    {
                      pageName: t(PageNames[activePage!]!).toLowerCase(),
                      pageSuffix:
                        activePage === PageType.POST_PURCHASE_PAGE1 || activePage === PageType.POST_PURCHASE_PAGE2
                          ? " page"
                          : ""
                    }
                  )}
                  maxWidth={"285px"}
                >
                  <span>
                    <Icons.Question color={"neutrals.700"} />
                  </span>
                </ToolTip>
              </Flex>
              <Flex justifyContent={"center"} alignItems={"center"}>
                <Flex pr={5} gap="12px">
                  {isAnalyticView && (
                    <Box height="36px" background="#ffffff" borderRadius="6px">
                      <DateRangePicker
                        id="date"
                        name="MM / DD"
                        defaultFromDate={new Date(dateRange.startDate)}
                        defaultToDate={new Date(dateRange?.endDate)}
                        onDateValueChange={(startDate: Date | undefined, endDate: Date | undefined) => {
                          if (startDate && endDate) {
                            dispatch(handleDateRangePicker({ startDate: startDate, endDate: endDate }));
                          }
                        }}
                        size="sm"
                        shortcut={shortcut}
                        setShortCut={setShortCut}
                      />
                    </Box>
                  )}
                  <Flex gap="12px" wrap={"wrap"}>
                  <ToolTip placement="top" label={t("Analytics")}>
                    <Flex
                      p="8px"
                      height={"36px"}
                      width={"36px"}
                      bg={isAnalyticView ? "neutrals.100" : "neutrals.50"}
                      borderRadius={4}
                      onClick={() => dispatch(toggleAnalyticsView())}
                      cursor={"pointer"}
                      _hover={{ background: "neutrals.100" }}
                      onMouseEnter={() => setIsAnalyticsHovered(true)}
                      onMouseLeave={() => setIsAnalyticsHovered(false)}
                    >
                      <Icons.Analytics
                        height={"20px"}
                        width={"20px"}
                        color={isAnalyticView || isAnalyticsHovered ? "neutrals.700" : "neutrals.400"}
                      />
                    </Flex>
                  </ToolTip>
                  <ToolTip placement="top" label={t("Widgets")}>
                    <Flex
                      p="8px"
                      height={"36px"}
                      width={"36px"}
                      bg={isWidgetView ? "neutrals.100" : "neutrals.50"}
                      borderRadius={4}
                      onClick={() => dispatch(toggleWidgetView())}
                      cursor={"pointer"}
                      _hover={{ background: "neutrals.100" }}
                      onMouseEnter={() => setIsWidgetHovered(true)}
                      onMouseLeave={() => setIsWidgetHovered(false)}
                    >
                      <Icons.Widgets color={isWidgetView || isWidgetHovered ? "neutrals.700" : "neutrals.400"} />
                    </Flex>
                  </ToolTip>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <Flex my="auto">{APP_PLATFORM === Platform.Shopify ? <SlotsHeaderLinks /> : <></>}</Flex>
          </Flex>
        </Flex>
        {!isPageLoading && APP_PLATFORM === Platform.Wix && activePage === PageType.PRE_PURCHASE_PRODUCT_PAGE && (
          <SlotBanner slots={slots} selectedCondition={selectedCondition} />
        )}
        <Divider
          ml="5px"
          orientation="horizontal"
          my="16px"
          width={containerWidth ? containerWidth : "100%"}
          maxWidth={isSmallScreen ? "100%" : "748px"}
        />
        {undefinedSlot && (
          <Box mx={"16px"} mt={3} p={2}>
            <Banner
              title={`${t("Action Required: your new slot isn't active")}`}
              status={"warning" as any}
              description={t("You've placed {{slotName}} on your store, but it won't show anything until you create and configure the slot in the Flow Builder.", { slotName: t(undefinedSlot?.slotName) })}
              primaryButton={{
                label: t("Create a slot"),
                onClick: () => handleSlotCreationBySlotNumber(),
                variant: "link",
              }}
            />
          </Box>
        )}
        {canCreateSlot && (
          <Button
            variant="secondary"
            width={containerWidth ? containerWidth : "100%"}
            mb="16px"
            onClick={createSlot}
            isDisabled={!selectedCondition}
            isLoading={creatingSlot}
            color="neutrals.900"
            padding={"0px 5px 0px 5px"}
            maxWidth={isSmallScreen ? "100%" : "748px"}
            ml="5px"
            rightIcon={<Icons.Plus cursor="pointer" height={"16px"} width={"16px"} />}
          >
            {t("Create a slot")}
          </Button>
        )}
      </Box>
      <Box
        overflowY={"auto"}
        flex={1}
        minHeight={0}
        height={`calc(100vh - ${canCreateSlot ? "190px" : `${APP_PLATFORM === Platform.Shopify ? "120px" : "205px"}`} )`}
        padding={"0px 5px 0px 5px"}
      >
        {loadingSlots || loading || loadingCondition ? (
          <Center>
            <Spinner variant={"ring"} size={"lg"} mt={20} />
          </Center>
        ) : (
          <>
            {!selectedCondition ? (
              <EmptyCard />
            ) : (
              <Box ref={containerRef}>
                <Card
                  pb={4}
                  background="rgba(255, 255, 255, 0.50)"
                  borderTop={"1px solid"}
                  borderTopColor={"neutrals.200"}
                  borderTopRadius={"8px"}
                  height="auto"
                  minHeight={`calc(100vh - ${isWidth1090 ? "126px" : "80px"})`}
                  maxWidth={isSmallScreen ? "100%" : "748px"}
                  ref={measuredRef}
                >
                  <Box ml={"-5px"} mt={"8px"}>
                    <Badge colorScheme="#9A8318" size="large" variant="sharp" maxWidth={"60%"}>
                      <Typography
                        variant="body-16-medium"
                        style={{ zIndex: "2", overflow: "hidden", textOverflow: "ellipsis" }}
                        textTransform="uppercase"
                      >
                        {t(selectedCondition?.name)}
                      </Typography>
                    </Badge>
                  </Box>

                  {slots && slots.length > 0 ? (
                    <>
                      {slots.map((slot: any, index: number) => {
                        return <SlotVariant slot={slot} variants={variantsBySlot[`${slot?.id}`]} key={index} />;
                      })}
                      {canCreateSlot && <Divider orientation="horizontal" mt={"32px"} />}
                      {canCreateSlot && (
                        <Flex justifyContent="center" alignItems="center" mt={6} gap={2} color="neutrals.700">
                          <Button
                            size="sm"
                            variant="outline"
                            padding={"4px 8px"}
                            height={"min-content"}
                            color="neutrals.700"
                            border={"1px solid"}
                            borderColor={"neutrals.700"}
                            _hover={{
                              color: "neutrals.900",
                              border: "1px solid",
                              borderColor: "neutrals.900",
                              bg: "neutrals.50",
                            }}
                            _active={{ color: "#ffffff", bg: "neutrals.900" }}
                            gap={1}
                            onClick={createSlot}
                            display={"flex"}
                            isDisabled={!selectedCondition}
                            isLoading={creatingSlot}
                            _loading={{
                              height: "25px",
                            }}
                          >
                            <Typography variant="xsmall">{t("Create a slot")}</Typography>
                            <Icons.Plus height={"12px"} width={"12px"} />
                          </Button>
                        </Flex>
                      )}
                    </>
                  ) : (
                    <Flex flexDirection="column" justifyContent="center" alignItems="center" mt={4}>
                      <Typography variant="h5-medium" color="neutrals.500">
                        {t("Please create a slot")}
                      </Typography>
                      <Button
                        onClick={createSlot}
                        px={4}
                        mt={4}
                        isLoading={creatingSlot}
                        rightIcon={<Icons.Plus height={"16px"} width={"16px"} />}
                      >
                        {t("Create a slot")}
                      </Button>
                    </Flex>
                  )}
                </Card>
              </Box>
            )}
          </>
        )}
      </Box>
      {APP_PLATFORM === Platform.Shopify && (
        <PreMadeTemplateModal
          showPreMadeTemplateModal={showPreMadeTemplateModal}
          onClose={() => setShowPreMadeTemplateModal(false)}
          slotNo={null}
          setShowPreMadeTemplateModal={setShowPreMadeTemplateModal}
          slotType={null}
        />
      )}
    </Box>
  );
};

export default Slots;
